/**
 * Folding the log into state, through the amendment overlay.
 *
 * The log is append-only. A correction never edits or deletes a past event;
 * it appends a directive *about* one:
 *
 *   void  -- treat the target as though it never happened (this is undo)
 *   amend -- shallow-patch the target's payload ("the dice were 6,5,2")
 *   set   -- adjust a territory in place, at the correction's own position
 *
 * So a mistyped die from three turns ago is fixable without rewriting history,
 * and the replay can show both readings: `fold` gives the corrected account,
 * `foldAsPlayed` gives what people actually did at the table.
 */

import type { EventId } from '../ids.js';
import type { Directive, RecordedEvent } from '../events/events.js';
import { apply } from './reducer.js';
import type { Viewer } from './redact.js';
import { EMPTY_STATE, type GameState } from './types.js';

export interface FoldOptions {
  /** Whose view to build. Defaults to the server's omniscient view. */
  readonly viewer?: Viewer;
  readonly initial?: GameState;
  /** Ignore void/amend directives, reproducing the log as it was played. */
  readonly asPlayed?: boolean;
}

export interface Overlay {
  readonly voided: ReadonlySet<EventId>;
  readonly patches: ReadonlyMap<EventId, Readonly<Record<string, unknown>>>;
}

/**
 * Resolve which directives actually take effect.
 *
 * A correction can only ever target an event that already exists, so a
 * correction can only be cancelled by a *later* one. Walking the corrections
 * newest-first therefore settles the question in a single pass: the newest is
 * always in force, and each older one counts only if nothing still in force
 * has voided it. Undoing an undo restores the original, and undoing that
 * removes it again, with no fixpoint iteration and no chance of oscillation.
 */
export function buildOverlay(events: readonly RecordedEvent[]): Overlay {
  const corrections = events.filter(
    (event): event is RecordedEvent & { type: 'correction.applied' } =>
      event.type === 'correction.applied',
  );

  const voided = new Set<EventId>();
  for (let i = corrections.length - 1; i >= 0; i -= 1) {
    const correction = corrections[i]!;
    if (voided.has(correction.id)) continue;
    const directive: Directive = correction.payload.directive;
    if (directive.op === 'void') voided.add(directive.target);
  }

  const patches = new Map<EventId, Record<string, unknown>>();
  for (const correction of corrections) {
    if (voided.has(correction.id)) continue;
    const directive: Directive = correction.payload.directive;
    if (directive.op !== 'amend') continue;
    // Later amendments to the same event win field by field.
    patches.set(directive.target, { ...patches.get(directive.target), ...directive.patch });
  }

  return { voided, patches };
}

export function fold(events: readonly RecordedEvent[], options: FoldOptions = {}): GameState {
  const viewer: Viewer = options.viewer ?? 'omniscient';
  const initial = options.initial ?? EMPTY_STATE;
  if (options.asPlayed === true) {
    return events.reduce((state, event) => apply(state, event, { viewer }), initial);
  }

  const overlay = buildOverlay(events);
  let state = initial;
  for (const event of events) {
    if (overlay.voided.has(event.id)) continue;
    state = apply(state, patched(event, overlay), { viewer });
  }
  return state;
}

/** The log as it was played, ignoring every later correction. */
export function foldAsPlayed(
  events: readonly RecordedEvent[],
  options: Omit<FoldOptions, 'asPlayed'> = {},
): GameState {
  return fold(events, { ...options, asPlayed: true });
}

/**
 * Amendments patch payload fields only. Restricting them to the payload keeps
 * a correction from rewriting an event's identity, sequence or author, so the
 * log's provenance survives any correction.
 */
function patched(event: RecordedEvent, overlay: Overlay): RecordedEvent {
  const patch = overlay.patches.get(event.id);
  if (patch === undefined) return event;
  return { ...event, payload: { ...event.payload, ...patch } } as RecordedEvent;
}
